import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { articles } from '../data/articles';

const { FiX, FiSearch, FiArrowRight, FiFileText, FiLayers } = FiIcons;

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ articles: [], services: [] });
  const inputRef = useRef(null);

  const services = [
    { title: 'Strategy & Advisory', path: '/mentorship/strategy-advisory', description: 'Vision clarity and strategic planning' },
    { title: 'AI & Automation', path: '/mentorship/ai-automation', description: 'Practical AI implementation and workflows' },
    { title: 'Video & Media', path: '/mentorship/video-media', description: 'Storytelling infrastructure' },
    { title: 'Creative Consulting', path: '/mentorship/creative-consulting', description: 'Brand clarity and creative direction' },
    { title: 'Digital Products', path: '/shop/digital-products', description: 'Platform architecture and web experiences' },
    { title: 'Crypto Education', path: '/mentorship/crypto-education', description: 'Blockchain literacy and security education' },
    { title: 'Crypto Consulting', path: '/mentorship/crypto-consulting', description: 'Strategic infrastructure for web3' },
    { title: 'Investments & Ventures', path: '/mentorship/investments-ventures', description: 'Selective participation in ventures' },
    { title: 'Property Management', path: '/mentorship/property-management', description: 'Asset oversight and stewardship' },
    { title: 'Community & Events', path: '/mentorship/community-events', description: 'Educational gatherings and community building' },
    { title: 'Special Projects', path: '/mentorship/special-projects', description: 'Cross-disciplinary experiments' }
  ];

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.length < 2) {
      setResults({ articles: [], services: [] });
      return;
    }

    const searchQuery = query.toLowerCase();

    // Search articles
    const articleResults = articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery) ||
      article.excerpt.toLowerCase().includes(searchQuery) ||
      article.category.toLowerCase().includes(searchQuery) ||
      article.tags?.some(tag => tag.toLowerCase().includes(searchQuery))
    ).slice(0, 5);

    // Search services
    const serviceResults = services.filter(service =>
      service.title.toLowerCase().includes(searchQuery) ||
      service.description.toLowerCase().includes(searchQuery)
    ).slice(0, 5);

    setResults({
      articles: articleResults,
      services: serviceResults
    });
  }, [query]);

  const handleClose = () => {
    setQuery('');
    setResults({ articles: [], services: [] });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-start justify-center pt-20 px-4"
        onClick={handleClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-modal-title"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center space-x-4">
              <SafeIcon icon={FiSearch} className="w-6 h-6 text-slate-400" aria-hidden="true" />
              <label htmlFor="search-input" className="sr-only">
                Search articles, services, and resources
              </label>
              <input
                id="search-input"
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles, services, and resources..."
                className="flex-1 text-lg outline-none text-slate-900 placeholder-slate-400"
                aria-describedby="search-instructions"
              />
              <button
                onClick={handleClose}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Close search"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-slate-500" aria-hidden="true" />
              </button>
            </div>
            <p id="search-instructions" className="sr-only">
              Type at least 2 characters to search. Results will appear below as you type.
            </p>
          </div>

          {/* Results */}
          <div className="overflow-y-auto max-h-[calc(80vh-100px)]" role="region" aria-live="polite" aria-atomic="true">
            {query.length < 2 ? (
              <div className="p-12 text-center text-slate-500">
                <SafeIcon icon={FiSearch} className="w-12 h-12 mx-auto mb-4 text-slate-300" aria-hidden="true" />
                <p>Start typing to search...</p>
              </div>
            ) : results.articles.length === 0 && results.services.length === 0 ? (
              <div className="p-12 text-center text-slate-500" role="status">
                <p>No results found for "{query}"</p>
              </div>
            ) : (
              <div className="p-6 space-y-8">
                {/* Services Results */}
                {results.services.length > 0 && (
                  <section aria-labelledby="services-heading">
                    <div className="flex items-center space-x-2 mb-4">
                      <SafeIcon icon={FiLayers} className="w-5 h-5 text-slate-600" aria-hidden="true" />
                      <h3 id="services-heading" className="font-bold text-slate-900 uppercase tracking-wider text-sm">
                        Services
                      </h3>
                    </div>
                    <div className="space-y-2">
                      {results.services.map((service) => (
                        <Link
                          key={service.path}
                          to={service.path}
                          onClick={handleClose}
                          className="block p-4 rounded-lg hover:bg-slate-50 transition-colors group"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-slate-900 group-hover:text-slate-700">
                                {service.title}
                              </h4>
                              <p className="text-sm text-slate-600 mt-1">
                                {service.description}
                              </p>
                            </div>
                            <SafeIcon
                              icon={FiArrowRight}
                              className="w-5 h-5 text-slate-400 group-hover:text-slate-900 group-hover:translate-x-1 transition-all"
                              aria-hidden="true"
                            />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                {/* Articles Results */}
                {results.articles.length > 0 && (
                  <section aria-labelledby="articles-heading">
                    <div className="flex items-center space-x-2 mb-4">
                      <SafeIcon icon={FiFileText} className="w-5 h-5 text-slate-600" aria-hidden="true" />
                      <h3 id="articles-heading" className="font-bold text-slate-900 uppercase tracking-wider text-sm">
                        Articles
                      </h3>
                    </div>
                    <div className="space-y-2">
                      {results.articles.map((article) => (
                        <Link
                          key={article.slug}
                          to={`/articles/read/${article.slug}`}
                          onClick={handleClose}
                          className="block p-4 rounded-lg hover:bg-slate-50 transition-colors group"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                  {article.category}
                                </span>
                                <span className="text-xs text-slate-400" aria-hidden="true">•</span>
                                <span className="text-xs text-slate-400">{article.readTime}</span>
                              </div>
                              <h4 className="font-semibold text-slate-900 group-hover:text-slate-700 mb-1">
                                {article.title}
                              </h4>
                              <p className="text-sm text-slate-600 line-clamp-2">
                                {article.excerpt}
                              </p>
                            </div>
                            <SafeIcon
                              icon={FiArrowRight}
                              className="w-5 h-5 text-slate-400 group-hover:text-slate-900 group-hover:translate-x-1 transition-all flex-shrink-0 ml-4 mt-1"
                              aria-hidden="true"
                            />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SearchModal;